import { useState, useEffect } from "react";
import { ExternalLink, Calendar, CreditCard, Globe, CheckCircle2, Plus, X, Loader2, GripVertical, CalendarIcon, AlertCircle } from "lucide-react";
import { format, isPast, differenceInDays } from "date-fns";
import { fr } from "date-fns/locale";
import { db } from "@/lib/supabaseUntyped";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface DemarcheStep {
  id: string;
  label: string;
  step_type: string;
  is_completed: boolean;
  due_date: string | null;
  url: string | null;
  notes: string | null;
  sort_order: number;
  completed_at: string | null;
}

interface DemarcheStepsListProps {
  demarcheId: string;
  steps: DemarcheStep[];
  onUpdate: () => void;
}

const STEP_TYPE_CONFIG = {
  task: { icon: CheckCircle2, label: 'Tâche', color: 'text-muted-foreground' },
  rdv: { icon: Calendar, label: 'RDV', color: 'text-blue-500' },
  payment: { icon: CreditCard, label: 'Paiement', color: 'text-green-500' },
  online: { icon: Globe, label: 'En ligne', color: 'text-purple-500' },
};

interface SortableStepItemProps {
  step: DemarcheStep;
  index: number;
  isLoading: boolean;
  onToggleComplete: (step: DemarcheStep) => void;
  onDeleteStep: (stepId: string) => void;
}

function SortableStepItem({
  step,
  index,
  isLoading,
  onToggleComplete,
  onDeleteStep,
}: SortableStepItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const config = STEP_TYPE_CONFIG[step.step_type as keyof typeof STEP_TYPE_CONFIG] || STEP_TYPE_CONFIG.task;
  const Icon = config.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-start gap-2 p-3 rounded-lg border transition-colors",
        step.is_completed 
          ? "bg-green-500/5 border-green-500/20" 
          : "bg-muted/30 border-border",
        isDragging && "shadow-lg ring-2 ring-primary/20"
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="touch-none p-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <Checkbox
        checked={step.is_completed}
        onCheckedChange={() => onToggleComplete(step)}
        disabled={isLoading}
        className="mt-0.5"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-xs text-muted-foreground font-mono">
            {index + 1}.
          </span>
          <span className={cn(
            "text-sm font-medium flex-1",
            step.is_completed && "line-through text-muted-foreground"
          )}>
            {step.label}
          </span>
          <Badge variant="outline" className={cn("text-[10px] gap-1", config.color)}>
            <Icon className="w-2.5 h-2.5" />
            {config.label}
          </Badge>
        </div>

        {step.due_date && !step.is_completed && (
          <p className={cn(
            "text-xs flex items-center gap-1",
            isPast(new Date(step.due_date)) 
              ? "text-destructive" 
              : differenceInDays(new Date(step.due_date), new Date()) <= 3
                ? "text-amber-600 dark:text-amber-400"
                : "text-muted-foreground"
          )}>
            {isPast(new Date(step.due_date)) && <AlertCircle className="w-3 h-3" />}
            <CalendarIcon className="w-3 h-3" />
            {isPast(new Date(step.due_date)) 
              ? `En retard (${format(new Date(step.due_date), 'd MMM', { locale: fr })})`
              : `Pour le ${format(new Date(step.due_date), 'd MMM yyyy', { locale: fr })}`
            }
          </p>
        )}

        {step.notes && (
          <p className="text-xs text-muted-foreground mb-1">
            {step.notes}
          </p>
        )}

        {step.completed_at && step.is_completed && (
          <p className="text-xs text-green-600 dark:text-green-400">
            Terminé le {format(new Date(step.completed_at), 'd MMM yyyy', { locale: fr })}
          </p>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {step.url && !step.is_completed && (
          <Button
            variant="outline"
            size="sm"
            asChild
          >
            <a href={step.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-3.5 h-3.5 mr-1" />
              Ouvrir
            </a>
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={() => onDeleteStep(step.id)}
          disabled={isLoading}
          title="Supprimer l'étape"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export function DemarcheStepsList({ demarcheId, steps, onUpdate }: DemarcheStepsListProps) {
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [addStepDialogOpen, setAddStepDialogOpen] = useState(false);
  const [newStepLabel, setNewStepLabel] = useState("");
  const [newStepType, setNewStepType] = useState<string>("task");
  const [newStepUrl, setNewStepUrl] = useState("");
  const [newStepDueDate, setNewStepDueDate] = useState<Date | undefined>(undefined);
  const [isAddingStep, setIsAddingStep] = useState(false);
  const [localSteps, setLocalSteps] = useState<DemarcheStep[]>(steps);
  const [isDragging, setIsDragging] = useState(false);

  // Keep local state in sync with props only when steps content changes (not just order)
  useEffect(() => {
    if (!isDragging) {
      // Only sync if the actual items changed, not just the order
      const localIds = new Set(localSteps.map(s => s.id));
      const propsIds = new Set(steps.map(s => s.id));
      const idsMatch = localIds.size === propsIds.size && [...localIds].every(id => propsIds.has(id));
      
      if (!idsMatch) {
        setLocalSteps(steps);
      }
    }
  }, [steps, isDragging]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setIsDragging(false);
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = localSteps.findIndex(s => s.id === active.id);
    const newIndex = localSteps.findIndex(s => s.id === over.id);
    
    const newOrder = arrayMove(localSteps, oldIndex, newIndex);
    setLocalSteps(newOrder);

    // Update sort_order in database
    try {
      const updates = newOrder.map((step, index) => ({
        id: step.id,
        sort_order: index,
      }));

      for (const update of updates) {
        await db
          .from('demarche_steps')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);
      }
    } catch (error) {
      console.error('Error updating step order:', error);
      toast.error("Erreur lors de la réorganisation");
      setLocalSteps(steps); // Revert to original order
    }
  };

  const handleToggleComplete = async (step: DemarcheStep) => {
    setUpdatingId(step.id);
    try {
      const updates: any = { 
        is_completed: !step.is_completed,
        completed_at: !step.is_completed ? new Date().toISOString() : null,
      };

      const { error } = await db
        .from('demarche_steps')
        .update(updates)
        .eq('id', step.id);

      if (error) throw error;
      onUpdate();
    } catch (error) {
      console.error('Error updating step:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAddStep = async () => {
    if (!newStepLabel.trim()) {
      toast.error("Veuillez saisir une description pour l'étape");
      return;
    }

    setIsAddingStep(true);
    try {
      const maxOrder = localSteps.length > 0 
        ? Math.max(...localSteps.map(s => s.sort_order)) 
        : 0;

      const { error } = await db
        .from('demarche_steps')
        .insert({
          demarche_id: demarcheId,
          label: newStepLabel.trim(),
          step_type: newStepType,
          url: newStepType === 'online' && newStepUrl.trim() ? newStepUrl.trim() : null,
          due_date: newStepDueDate ? newStepDueDate.toISOString() : null,
          is_completed: false,
          sort_order: maxOrder + 1,
        });

      if (error) throw error;

      toast.success("Étape ajoutée");
      setNewStepLabel("");
      setNewStepType("task");
      setNewStepUrl("");
      setNewStepDueDate(undefined);
      setAddStepDialogOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding step:', error);
      toast.error("Erreur lors de l'ajout");
    } finally {
      setIsAddingStep(false);
    }
  };

  const handleDeleteStep = async (stepId: string) => {
    setUpdatingId(stepId);
    try {
      const { error } = await db
        .from('demarche_steps')
        .delete()
        .eq('id', stepId);

      if (error) throw error;
      toast.success("Étape supprimée");
      onUpdate();
    } catch (error) {
      console.error('Error deleting step:', error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <>
      <div className="space-y-2">
        {localSteps.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-3">Aucune étape définie</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddStepDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une étape
            </Button>
          </div>
        ) : (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localSteps.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {localSteps.map((step, index) => (
                  <SortableStepItem
                    key={step.id}
                    step={step}
                    index={index}
                    isLoading={updatingId === step.id}
                    onToggleComplete={handleToggleComplete}
                    onDeleteStep={handleDeleteStep}
                  />
                ))}
              </SortableContext>
            </DndContext>

            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 border border-dashed text-muted-foreground hover:text-foreground"
              onClick={() => setAddStepDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une étape
            </Button>
          </>
        )}
      </div>

      {/* Add Step Dialog */}
      <Dialog open={addStepDialogOpen} onOpenChange={setAddStepDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Ajouter une étape
            </DialogTitle>
            <DialogDescription>
              Ajoutez une étape personnalisée à cette démarche
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="stepLabel">Description de l'étape</Label>
              <Input
                id="stepLabel"
                placeholder="Ex: Prendre RDV en préfecture"
                value={newStepLabel}
                onChange={(e) => setNewStepLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isAddingStep && newStepLabel.trim()) {
                    handleAddStep();
                  }
                }}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="stepType">Type d'étape</Label>
              <Select value={newStepType} onValueChange={setNewStepType}>
                <SelectTrigger id="stepType">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="task">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Tâche
                    </div>
                  </SelectItem>
                  <SelectItem value="rdv">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      RDV
                    </div>
                  </SelectItem>
                  <SelectItem value="payment">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-green-500" />
                      Paiement
                    </div>
                  </SelectItem>
                  <SelectItem value="online">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-purple-500" />
                      En ligne
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newStepType === 'online' && (
              <div className="space-y-2">
                <Label htmlFor="stepUrl">URL (optionnel)</Label>
                <Input
                  id="stepUrl"
                  type="url"
                  placeholder="https://..."
                  value={newStepUrl}
                  onChange={(e) => setNewStepUrl(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Date d'échéance (optionnel)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !newStepDueDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newStepDueDate ? (
                      format(newStepDueDate, "d MMMM yyyy", { locale: fr })
                    ) : (
                      "Sélectionner une date"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={newStepDueDate}
                    onSelect={setNewStepDueDate}
                    initialFocus
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
              {newStepDueDate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() => setNewStepDueDate(undefined)}
                >
                  <X className="w-3 h-3 mr-1" />
                  Supprimer la date
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddStepDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddStep} disabled={isAddingStep || !newStepLabel.trim()}>
              {isAddingStep ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ajout...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
